import { useEffect, useRef, useState } from 'react';
import type { useConsoleRecorder } from './useConsoleRecorder';
import type { VoiceDraft } from './consoleVoiceDraft';
import {
  transcriptStatus,
  voiceTime,
  VOICE_MAX_SECONDS,
  VOICE_TRANSCRIPT_MAX,
} from './consoleVoice';
import './consoleVoice.css';
export function ConsoleVoiceRecorder({
  recorder,
  hidden,
  onSend,
  onType,
}: {
  recorder: ReturnType<typeof useConsoleRecorder>;
  hidden: boolean;
  onSend: (draft: VoiceDraft) => Promise<void>;
  onType: () => void;
}) {
  const { phase, draft } = recorder;
  const [url, setUrl] = useState('');
  const [playable, setPlayable] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [addTranscript, setAddTranscript] = useState(false);
  const sheet = useRef<HTMLElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const sendLock = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    setPlayable(false);
    if (!draft?.blob.size || phase !== 'review') {
      setUrl('');
      return;
    }
    const next = URL.createObjectURL(draft.blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [draft?.blob, phase]);
  useEffect(() => {
    if (phase !== 'review') return;
    sheet.current?.querySelector('button')?.focus();
    return () => launcher.current?.focus();
  }, [phase]);
  function markReady(audio: HTMLAudioElement) {
    if (draft?.blob.size && Number.isFinite(audio.duration) && audio.duration > 0)
      setPlayable(true);
  }
  async function send() {
    if (!draft || !playable || sendLock.current) return;
    sendLock.current = true;
    setSending(true);
    setError('');
    try {
      await onSend(addTranscript ? draft : { ...draft, transcript: '', partial: false });
      if (alive.current) recorder.discard();
    } catch {
      if (alive.current) setError('Voice note did not send. Your draft is kept. Try again.');
    } finally {
      sendLock.current = false;
      if (alive.current) setSending(false);
    }
  }
  if (phase === 'review' && draft)
    return (
      <section
        ref={sheet}
        className="console-voice-review"
        role="dialog"
        aria-modal="true"
        aria-label="Review voice note"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !sending) recorder.keep();
          if (event.key === 'Tab') {
            const items = sheet.current?.querySelectorAll<HTMLElement>(
              'button:not(:disabled),textarea,audio',
            );
            if (!items?.length) return;
            const first = items[0],
              last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <header>
          <div>
            <h2>Review voice note</h2>
            <p>To Kyu · Private chat</p>
          </div>
          <button disabled={sending} onClick={recorder.keep} aria-label="Keep draft and close">
            ×
          </button>
        </header>
        <div className="console-voice-review-body">
          <p role="status">{recorder.notice}</p>
          {url ? (
            <audio
              src={url}
              controls
              preload="metadata"
              onCanPlay={() => setPlayable(true)}
              onLoadedMetadata={(event) => markReady(event.currentTarget)}
              onDurationChange={(event) => markReady(event.currentTarget)}
              onError={() => {
                setPlayable(false);
                setError(
                  'Audio was not recovered. Your transcript is kept; record again before sending.',
                );
              }}
            />
          ) : (
            <p>Audio was not recovered. Your transcript is kept.</p>
          )}
          {draft.transcript.trim() ? (
            <label className="console-voice-opt-in">
              <input
                type="checkbox"
                checked={addTranscript}
                disabled={sending}
                onChange={(event) => setAddTranscript(event.target.checked)}
              />
              Add transcript (made by your browser's recognition provider)
            </label>
          ) : null}
          {addTranscript ? (
            <>
              <label htmlFor="voice-transcript">Transcript · Tap to edit</label>
              <textarea
                id="voice-transcript"
                value={draft.transcript}
                disabled={sending}
                onChange={(event) => recorder.edit(event.target.value)}
              />
              <p>{draft.transcript.length.toLocaleString()} / 8,000 characters</p>
              <p>{transcriptStatus(draft.transcript, draft.partial)}</p>
              <p>Check names and details before sending. Your voice recording stays as recorded.</p>
            </>
          ) : null}
          <p>{recorder.persistence}</p>
          <p role="status">{error}</p>
          {!playable && !sending && url && !error ? <p role="status">Preparing audio…</p> : null}
        </div>
        <footer>
          <div>
            <button disabled={sending} onClick={recorder.discard}>
              Discard
            </button>
            <button disabled={sending} onClick={recorder.keep}>
              Keep draft
            </button>
          </div>
          <button
            className="console-voice-primary"
            disabled={
              !playable ||
              sending ||
              (addTranscript && draft.transcript.length > VOICE_TRANSCRIPT_MAX)
            }
            onClick={() => void send()}
          >
            {sending ? 'Uploading and sending…' : 'Send voice note'}
          </button>
        </footer>
      </section>
    );
  const recording = phase === 'recording';
  return (
    <div className="console-voice-dock" hidden={hidden && !recording}>
      {recording ? (
        <div className="console-voice-row-dock">
          <div className="console-voice-elapsed" aria-label="Recording elapsed">
            {voiceTime(recorder.elapsed)} / {voiceTime(VOICE_MAX_SECONDS)}
          </div>
          <button
            className="console-voice-primary console-voice-circle console-voice-stop"
            aria-label="Stop recording"
            onClick={() => recorder.stop()}
          >
            <svg data-icon="stop" width="30" height="30" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" fill="white" />
            </svg>
          </button>
        </div>
      ) : (
        <>
          {recorder.notice ? (
            <p role="status">
              {recorder.notice} <button onClick={onType}>Type a message</button>
            </p>
          ) : null}
          <button
            ref={launcher}
            className="console-voice-primary console-voice-circle"
            aria-label={
              phase === 'kept'
                ? 'Resume voice note'
                : phase === 'starting'
                  ? 'Starting…'
                  : 'Voice note'
            }
            disabled={phase === 'starting'}
            onClick={() => (phase === 'kept' ? recorder.resume() : void recorder.start())}
          >
            <svg data-icon="mic" width="30" height="30" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"
              />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
